import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import SkillCalculator from '../uTheory.Skills/skillCalculator';
import type { LastSharpOrFlatQuestion } from './keyByLastSharpOrFlat';
import KeyByLastSharpOrFlat from './keyByLastSharpOrFlat';

class LastSharpOrFlatInKey extends InteractivePrototype {
  skillCalculator: SkillCalculator;

  declare questions: LastSharpOrFlatQuestion[];

  declare currentQuestion: LastSharpOrFlatQuestion;

  private answer: string;

  wrongAnswerCount: number = 0;

  constructor(displayDiv: HTMLDivElement, options: InteractiveOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = LastSharpOrFlatInKey.registry.skills;
    this.settings = {
      instructions: {
        ariaLive: 'assertive',
        ariaLiveRegion: 'all',
      },
      editor: false,
      notation: false,
      piano: false,
      letters: {
        letterButtons: true,
      },
      checkMark: {},
      player: {},
      progressTracker: true,
      oneTryOnly: false,
    };

    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    this.skillCalculator = new SkillCalculator();

    // now call the prototype's init function:
    return super.init(displayDiv, options);
  }

  generateQuestions(questions: string[] = this.options.questions) {
    this.questionPrompt = 'What is the  <strong> last {question}</strong> in {question}?';
    if (!this.options.testMode) {
      this.questionPrompt += '<br /><small>{question[4]} of {question[5]}</small>';
    }

    const drill = this.options.drill !== undefined && this.options.drill === true;
    this.questions =
      drill === true ? this.generateDrillQuestions() : this.generateManualQuestions(questions);
  }

  generateManualQuestions(questions = this.options.questions): LastSharpOrFlatQuestion[] {
    return questions
      .map((question, index) => KeyByLastSharpOrFlat.processQuestion(question, index, questions))
      .filter((q) => q) as LastSharpOrFlatQuestion[];
  }

  generateDrillQuestions(): LastSharpOrFlatQuestion[] {
    const generator = new QuestionGenerator();
    const maxQuestions = this.options.maxQuestions || 10;
    const generatedQuestions = generator.generate(this.options.skills, maxQuestions, this.options);
    console.log(generatedQuestions);
    return generatedQuestions
      .map((question, index) =>
        KeyByLastSharpOrFlat.processQuestion(question, index, generatedQuestions)
      )
      .filter((q) => q) as LastSharpOrFlatQuestion[];
  }

  newQuestion() {
    super.newQuestion();
    const question = this.currentQuestion;
    this.letterPanel.setLettersAccidental(question.lastAccidentalNote.accidental);
  }

  newCheckpointQuestion(questionString) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  addListeners() {
    this.letterPanel.addLetterCallback(this.checkAnswer, this);
  }

  checkAnswer(letter) {
    if (this.rightAnswerDelay) return;
    const testMode = this.optionsTrue('testMode');
    const correct = letter === this.currentQuestion.lastAccidentalNote.letter;
    this.answer = letter + this.currentQuestion.lastAccidentalNote.accidental;

    this.wrongAnswerCount++;

    if (testMode === true) {
      this.checkpointAnswer(correct);
      return;
    }

    if (correct) {
      this.getAnswerAndData(true);
      this.rightAnswer(2000);
    } else {
      this.getAnswerAndData(false);
      this.wrongAnswer();
    }
  }

  playAnswer() {
    KeyByLastSharpOrFlat.prototype.playAnswer.call(this);
  }

  getAnswerAndData(correct) {
    const correctAnswer = this.currentQuestion.lastAccidentalNote.toShortString();
    const answerAndData = {
      question: this.currentQuestion.questionString,
      questionType: 'LastSharpOrFlatInKey',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: null,
      studentAnswerText: this.answer,
      studentAnswerSVG: null,
      correctAnswerText: correctAnswer,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };

    return answerAndData;
  }

  gameOver() {
    this.instructionsDiv.setAttribute('role', 'alert');
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.displayDiv.removeChild(this.lettersDiv);
    super.gameOver();
  }

  static registry = {
    description: 'Name the last accidental in a key',
    skills: ['sharp_majors', 'sharp_minors', 'flat_majors', 'flat_minors'],
  };
}

export default LastSharpOrFlatInKey;
